import { auth } from '../config/firebase.js';
import api from '../api/axios.js';
import axios from 'axios';

class AuthService {
    constructor() {
        this.lastRefreshTime = Date.now();
        this.refreshIntervalMs = 50 * 60 * 1000; // 50 minutes
        this.refreshTimer = null;
        this.setupTokenRefresh();
    }

    setupTokenRefresh() {
        this.scheduleNextRefresh();

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                const elapsed = Date.now() - this.lastRefreshTime;
                if (elapsed >= this.refreshIntervalMs) {
                    this.doTokenRefresh();
                }
            }
        });
    }

    scheduleNextRefresh() {
        clearTimeout(this.refreshTimer);
        const elapsed = Date.now() - this.lastRefreshTime;
        const remaining = Math.max(0, this.refreshIntervalMs - elapsed);
        this.refreshTimer = setTimeout(() => this.doTokenRefresh(), remaining);
    }

    async doTokenRefresh() {
        clearTimeout(this.refreshTimer);

        // Wall-clock guard: skip if we refreshed recently
        const elapsed = Date.now() - this.lastRefreshTime;
        if (elapsed < this.refreshIntervalMs) {
            this.scheduleNextRefresh();
            return;
        }

        const user = auth.currentUser;
        if (user) {
            try {
                const idToken = await user.getIdToken(true);
                await this.updateAuthCookie(idToken);
                this.lastRefreshTime = Date.now();
            } catch (error) {
                console.error('Proactive token refresh failed:', error);
                this.handleAuthError();
                return;
            }
        }
        this.scheduleNextRefresh();
    }

    async updateAuthCookie(idToken) {
        await axios.post('/api/auth/refresh-token', 
            { idToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                withCredentials: true
            }
        );

    }

    handleAuthError() {
        // Clear any local auth state and redirect to login
        auth.signOut();
        window.location.href = '/login';
    }

    // Method to manually refresh token (useful for API error handling)
    async refreshToken() {
        const user = auth.currentUser;
        if (user) {
            const idToken = await user.getIdToken(true);
            await this.updateAuthCookie(idToken);
            return idToken;
        }
        throw new Error('No authenticated user');
    }

    logout = async () => {
        try {
            // 1. Sign out from Firebase (client-side)
            if (auth.currentUser) {
                await auth.signOut();
            }

            // 2. Call server logout to clear cookie
            await api.post('/api/auth/logout');

            // 3. Clear any localStorage items
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');

            // 4. Redirect to login/home
            window.location.href = '/';

        } catch (error) {
            console.error('Logout error:', error);
            // Even if there's an error, still redirect
            window.location.href = '/';
        }
    };
}

// Create singleton instance
const authService = new AuthService();
export default authService;